import AddFacilityModal from '../../AddFacilityModal/AddFacilityModal'
import { MainApi } from 'api/MainApi'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import { canEditOrDeleteFacility, useUser } from 'hooks/user'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { IRobotInfo } from 'utils/interfaces'

import style from './FacilityItem.module.css'

export interface IFacilityInfo {
  id: number
  name: string
  facility_type: string
  street_address?: string
  city?: string
  country?: string
  postal_code?: string
  /** WGS84 base point (centre / entrance); MR-FACILITY-GEO. */
  geo_point?: { type: 'Point'; coordinates: number[] } | null
  robots_count: number
  robots_names: string[]
  robots_ids?: number[]
}

const FacilityItem = ({
  facility,
  onFacilitiesUpdated,
  robots,
}: {
  facility: IFacilityInfo
  onFacilitiesUpdated: () => void
  robots: IRobotInfo[]
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state: userState } = useUser()!
  const [deleteFacilityDialog, setDeleteFacilityDialog] = useState(false)
  const [editingFacility, setEditingFacility] = useState(false)

  const handleEditFacility = async (data: any) => {
    try {
      await MainApi.patchFacility(facility.id, data)
      setEditingFacility(false)
      onFacilitiesUpdated()
    } catch (error) {
      console.log('Error editing facility:', error)
    }
  }

  const deleteFacility = async () => {
    try {
      await MainApi.deleteFacility(facility.id)
      setDeleteFacilityDialog(false)
      onFacilitiesUpdated()
    } catch (error) {
      console.log('Error deleting facility:', error)
    }
  }

  const formatAddress = () => {
    const parts = [facility.street_address, facility.city, facility.country].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : '—'
  }

  const formatType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')
  }

  return (
    <>
      <div
        className={style.tableRow}
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(PATH.FACILITY + facility.id)}
      >
        <FavoriteStar kind="facility" id={facility.id} />
        <span className={style.tableCol}>{facility.name}</span>
        <span className={style.tableCol}>{formatType(facility.facility_type)}</span>
        <span className={style.tableCol}>{formatAddress()}</span>
        <span className={style.tableCol}>{facility.robots_count > 0 ? facility.robots_names.join(', ') : '—'}</span>
        <span className={style.tableCol} onClick={e => e.stopPropagation()}>
          {canEditOrDeleteFacility(userState) && (
            <ActionsMenu>
              <ActionsMenuItem onClick={() => setEditingFacility(true)} data-test-id={getTestId('edit-facility')}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                  {t('common.edit')}
                </span>
              </ActionsMenuItem>
              <ActionsMenuItem
                onClick={() => setDeleteFacilityDialog(true)}
                data-test-id={getTestId('delete-facility')}
              >
                <RemoveSvg className={style.menuItemIcon} />
                <span>{t('common.delete')}</span>
              </ActionsMenuItem>
            </ActionsMenu>
          )}
        </span>
      </div>
      <ConfirmDialog
        title="Delete the facility?"
        isOpen={deleteFacilityDialog}
        onClose={() => setDeleteFacilityDialog(false)}
        onConfirmClick={deleteFacility}
        confirmText={t('common.delete')}
        danger={true}
      />
      <AddFacilityModal
        isOpen={editingFacility}
        onClose={() => setEditingFacility(false)}
        onSave={handleEditFacility}
        robots={robots}
        facilityExisting={facility}
      />
    </>
  )
}

export default FacilityItem
